export type StripeSessions = Record<string, string>;

export type PremiumOffer = {
  id: string;
  card_required: boolean;
  description: string;
  expires_at: string | null;
  name: string;
  pct_discount: number | null;
  terms: string;
  custom_terms: string | null;
  trial_period_days: number | null;
  retention_offer: boolean;
};

export enum PriceInterval {
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
  ONE_TIME = 'one_time',
  DONATION = 'donation',
}

export interface Price {
  id: string;
  amount_cents: number;
  original_amount_cents: number;
  currency: string;
  interval: PriceInterval;
  denominator: string;
  cta: string;
  features: string[] | null;
  premium_offer?: PremiumOffer;
}

export interface Tier {
  id: string;
  name: string;
  description: string;
  prices: Price[];
  redirect_url: string;
}

export interface PricingDetails {
  gift_tiers: Tier[];
  tiers: Tier[];
}

export interface RetentionPremiumOffers {
  premium_offers: PremiumOffer[];
}

export interface StripePremiumSession {
  session: string;
}

export interface SubscriptionPause {
  id: string;
  paused_at: string;
  scheduled_resume_at: string;
  duration: number;
  time_unit: 'days' | 'weeks' | 'months';
}

export interface SubscriptionPremiumTier {
  id: string;
  name: string;
  permanent: boolean;
  gift_details: null | string;
  is_gift: boolean;
  tier_price_id: string;
  formatted_amount?: string;
  is_downgrading: boolean;
  cancel_at: string | null;
  price_preview?: Price | null;
  paused?: boolean;
  is_trialing: boolean;
  is_archived: boolean;
  pause_info?: SubscriptionPause;
}

export interface SubscriptionBillingSummary {
  pending_downgrade: boolean;
  prorated_downgrade_available: boolean;
  tier_name?: string;
  last_invoice?: {
    amount_cents?: number;
    cadence?: string;
    timestamp: string;
    currency?: string;
  };
  next_invoice?: {
    amount_cents: number;
    cadence: string;
    timestamp: string;
    currency: string;
  };
  price_id?: string;
}

export interface SubscriptionBillingDetails {
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1: string;
    line2: string;
    state: string;
    city: string;
    country: string;
    postal_code: string;
  };
  tax_ids?: [
    {
      type: string;
      value: string;
    },
  ];
}

export interface Subscription {
  id: number;
  status: string;
  tier: string;
  tier_names: string[];
  email: string;
  first_name: string;
  last_name: string;
  one_click_unsubscribe_status: null | boolean;
  stripe_customer_id: null | string;
  subscriber_id: string | undefined;
  avatar_url: string;
  comment_details_set: boolean;
  comment_publication_ban: boolean;
  comment_post_bans: string[];
  premium_tiers: SubscriptionPremiumTier[];
  password_exists: boolean;
  referrals_count: number;
  has_email_change_pending: boolean;
  pause_info?: SubscriptionPause;
  access_type: 'read-only' | 'write';
}
